import React from "react";
import { FaCheck, FaClipboard, FaRegCalendarCheck, FaUsers, FaChevronDown } from "react-icons/fa";

type IconComponent = React.ComponentType<{ className?: string }>;
const CheckIcon = FaCheck as IconComponent;
const ClipboardIcon = FaClipboard as IconComponent;
const RegCalendarCheckIcon = FaRegCalendarCheck as IconComponent;
const UsersIcon = FaUsers as IconComponent;
const ChevronDownIcon = FaChevronDown as IconComponent;

const CalendarSubscriptions = ({
  t,
  subscriptionsOpen,
  setSubscriptionsOpen,
  calendarLink,
  copyCalendarLink,
  copiedSchoolLink,
  selectedClassroomForIcal,
  setSelectedClassroomForIcal,
  setCopiedClassroomLink,
  yearClassrooms,
  entityId,
  classroomLabel,
  classroomCalendarLink,
  copyClassroomCalendarLink,
  copiedClassroomLink
}: any) => (
        <section className="lumen-plain-card rounded-2xl px-4 py-3">
          <button
            type="button"
            onClick={() => setSubscriptionsOpen((open) => !open)}
            className="flex w-full flex-col gap-2 text-left sm:flex-row sm:items-center sm:justify-between"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-indigo-700 dark:text-indigo-200">
              <RegCalendarCheckIcon />
              {t("schoolCalendar.subscriptions.title", {
                defaultValue: "Agenda-abonnementen",
              })}
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                {t("schoolCalendar.subscriptions.summary", {
                  defaultValue: "Schoolagenda en klasagenda links beschikbaar",
                })}
              </span>
            </span>

            <span className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-700 dark:text-indigo-200">
              {subscriptionsOpen
                ? t("schoolCalendar.subscriptions.hide", {
                    defaultValue: "Verberg links",
                  })
                : t("schoolCalendar.subscriptions.show", {
                    defaultValue: "Toon links",
                  })}
              <ChevronDownIcon
                className={`${subscriptionsOpen ? "rotate-180" : ""} transition`}
              />
            </span>
          </button>

          {subscriptionsOpen && (
            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
                  <RegCalendarCheckIcon />
                  {t("schoolCalendar.subscription.title", {
                    defaultValue: "Agenda-abonnement",
                  })}
                </div>

                <p className="text-xs text-gray-600 dark:text-gray-300">
                  {t("schoolCalendar.subscription.text", {
                    defaultValue:
                      "Gebruik deze link om de schoolagenda toe te voegen aan Apple Agenda, Google Calendar of Outlook.",
                  })}
                </p>

                <div className="flex flex-col gap-2 sm:flex-row">
                  <div className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-900/60 dark:text-gray-200">
                    <span className="block truncate" title={calendarLink}>
                      {calendarLink}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={copyCalendarLink}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-500"
                  >
                    {copiedSchoolLink ? <CheckIcon /> : <ClipboardIcon />}
                    {copiedSchoolLink
                      ? t("schoolCalendar.subscription.copied", {
                          defaultValue: "Gekopieerd",
                        })
                      : t("schoolCalendar.subscription.copy", {
                          defaultValue: "Kopieer agenda-link",
                        })}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
                  <UsersIcon />
                  {t("schoolCalendar.classSubscription.title", {
                    defaultValue: "Klasagenda",
                  })}
                </div>

                <p className="text-xs text-gray-600 dark:text-gray-300">
                  {t("schoolCalendar.classSubscription.text", {
                    defaultValue:
                      "Een klasagenda bevat klasactiviteiten voor deze klas en ook schoolbrede items.",
                  })}
                </p>

                <select
                  value={selectedClassroomForIcal}
                  onChange={(event) => {
                    setSelectedClassroomForIcal(event.target.value);
                    setCopiedClassroomLink(false);
                  }}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                >
                  <option value="">
                    {t("schoolCalendar.classSubscription.select", {
                      defaultValue: "Selecteer een klas",
                    })}
                  </option>

                  {yearClassrooms.map((classroom) => {
                    const id = String(entityId(classroom));
                    return (
                      <option key={id} value={id}>
                        {classroomLabel(classroom)}
                      </option>
                    );
                  })}
                </select>

                <div className="flex flex-col gap-2 sm:flex-row">
                  <div className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-900/60 dark:text-gray-200">
                    <span
                      className="block truncate"
                      title={classroomCalendarLink || ""}
                    >
                      {classroomCalendarLink ||
                        t("schoolCalendar.classSubscription.noClassroom", {
                          defaultValue: "Geen klas geselecteerd",
                        })}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={copyClassroomCalendarLink}
                    disabled={!classroomCalendarLink}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {copiedClassroomLink ? <CheckIcon /> : <ClipboardIcon />}
                    {copiedClassroomLink
                      ? t("schoolCalendar.classSubscription.copied", {
                          defaultValue: "Gekopieerd",
                        })
                      : t("schoolCalendar.classSubscription.copy", {
                          defaultValue: "Kopieer klasagenda-link",
                        })}
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
);

export default CalendarSubscriptions;
